import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Button,
  Skeleton,
  Alert,
  useTheme,
  useMediaQuery
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import MiscellaneousServicesIcon from '@mui/icons-material/MiscellaneousServices';
import { motion } from 'framer-motion';

const FeaturedServices = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get('/api/services', {
          params: {
            page: 1,
            limit: 6,
            status: 'active',
            orderBy: 'createdAt',
            order: 'DESC'
          }
        });

        if (response.data && response.data.services) {
          setServices(response.data.services);
        }
      } catch (error) {
        console.error('Error fetching services:', error);
        setError('Не удалось загрузить услуги');
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, []);

  // Форматирование цены в зависимости от типа
  const formatPrice = (service) => {
    if (service.priceType === 'fixed') {
      return `${service.basePrice} ₽`;
    } else if (service.priceType === 'hourly') {
      return `от ${service.hourlyPrice} ₽/час`;
    } else if (service.priceType === 'daily') {
      return `от ${service.dailyPrice} ₽/день`;
    } else if (service.priceType === 'range') {
      return `от ${service.basePrice} ₽`;
    } else {
      return 'Цена по запросу';
    }
  };

  // Анимация для элементов
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  // Заглушка для загрузки
  if (loading) {
    return (
      <Box component="section" sx={{ py: 8, backgroundColor: theme.palette.background.default }}>
        <Container>
          <Box sx={{ mb: 6, textAlign: 'center' }}>
            <Typography variant="h4" component="h2" gutterBottom>
              Наши услуги
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Загрузка услуг...
            </Typography>
          </Box>
          <Grid container spacing={3}>
            {[1, 2, 3].map((item) => (
              <Grid item xs={12} sm={6} md={4} key={item}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Skeleton variant="rectangular" height={200} />
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Skeleton variant="text" height={32} width="80%" />
                    <Skeleton variant="text" height={20} width="60%" />
                    <Skeleton variant="text" height={60} />
                  </CardContent>
                  <CardActions>
                    <Skeleton variant="rectangular" height={36} width={120} />
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    );
  }

  // Сообщение об ошибке
  if (error) {
    return (
      <Box component="section" sx={{ py: 8, backgroundColor: theme.palette.background.default }}>
        <Container>
          <Alert severity="error" sx={{ mb: 4 }}>
            {error}
          </Alert>
        </Container>
      </Box>
    );
  }

  // Если нет услуг, не показываем секцию
  if (services.length === 0) {
    return null;
  }

  return (
    <Box component="section" sx={{ py: 8, backgroundColor: theme.palette.background.default }}>
      <Container>
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={containerVariants}
        >
          <Box sx={{ mb: 6, textAlign: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
              <MiscellaneousServicesIcon color="primary" sx={{ fontSize: 32, mr: 1 }} />
              <Typography variant="h4" component="h2">
                Наши услуги
              </Typography>
            </Box>
            <Typography variant="subtitle1" color="text.secondary" sx={{ maxWidth: 700, mx: 'auto' }}>
              Мы предлагаем широкий спектр услуг для вашего комфортного отдыха
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {services.map((service) => (
              <Grid item xs={12} sm={6} md={4} key={service.id}>
                <motion.div variants={itemVariants}>
                  <Card 
                    sx={{ 
                      height: '100%', 
                      display: 'flex', 
                      flexDirection: 'column',
                      transition: 'transform 0.3s, box-shadow 0.3s',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: theme.shadows[10],
                      }
                    }}
                  >
                    <CardMedia
                      component="img"
                      height={200}
                      image={service.featuredImage || '/assets/placeholder-service.jpg'}
                      alt={service.name}
                      sx={{ objectFit: 'cover' }}
                    />
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Typography variant="h6" component="h3" gutterBottom>
                        {service.name}
                      </Typography>
                      <Typography variant="subtitle1" color="primary" gutterBottom>
                        {formatPrice(service)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                      }}>
                        {service.description}
                      </Typography>
                    </CardContent>
                    <CardActions>
                      <Button 
                        component={RouterLink} 
                        to={`/services/${service.slug || service.id}`} 
                        endIcon={<ArrowForwardIcon />}
                        sx={{ ml: 'auto' }}
                      >
                        Подробнее
                      </Button>
                    </CardActions>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ mt: 5, textAlign: 'center' }}>
            <Button 
              component={RouterLink} 
              to="/services" 
              variant="contained" 
              color="primary" 
              endIcon={<ArrowForwardIcon />}
              size="large"
            >
              Все услуги
            </Button>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default FeaturedServices;
